/**
 * Editable Phone Directive
 * Adds phone number formatting to x-editable phone fields
 * 
 * Usage:
 *   <a href="#" 
 *      editable-tel="model.phone" 
 *      editable-phone
 *      buttons="no" 
 *      blur="submit" 
 *      onaftersave="saveCallback($data)">
 *     {{model.phone || 'Click to add phone'}}
 *   </a>
 */
function editablePhoneDirective($timeout) {
  'ngInject';

  return {
    restrict: 'A',
    link: function(scope, element, attrs) {
      let inputElement = null;
      let checkInterval = null;

      // Function to format phone number
      function formatPhoneNumber(value) {
        if (!value) {
          return '';
        }

        // Remove all non-digit characters
        const cleaned = ('' + value).replace(/\D/g, '');
        
        // If empty after cleaning, return empty string
        if (!cleaned) {
          return '';
        }

        // Limit to 10 digits
        const digits = cleaned.substring(0, 10);

        // Format based on length
        let formatted = '';
        if (digits.length <= 3) {
          formatted = digits.length > 0 ? '(' + digits : '';
        } else if (digits.length <= 6) {
          formatted = '(' + digits.substring(0, 3) + ') ' + digits.substring(3);
        } else {
          formatted = '(' + digits.substring(0, 3) + ') ' + digits.substring(3, 6) + '-' + digits.substring(6);
        }

        return formatted;
      }

      // Function to initialize phone formatting on the input
      function initializePhoneFormatting() {
        if (!inputElement) {
          return;
        }

        // Set maxlength to 14 (for formatted phone: (XXX) XXX-XXXX)
        inputElement.setAttribute('maxlength', '14');

        // Format on input
        const inputHandler = function() {
          const formatted = formatPhoneNumber(inputElement.value);
          if (formatted !== inputElement.value) {
            const cursorPosition = inputElement.selectionStart;
            inputElement.value = formatted;
            
            // Try to maintain cursor position
            const newCursorPosition = Math.min(cursorPosition, formatted.length);
            $timeout(function() {
              inputElement.setSelectionRange(newCursorPosition, newCursorPosition);
            }, 0);
            
            // Update ng-model
            const ngModelCtrl = angular.element(inputElement).controller('ngModel');
            if (ngModelCtrl) {
              ngModelCtrl.$setViewValue(formatted);
              ngModelCtrl.$render();
            }
          }
        };

        // Format on blur to ensure final format
        const blurHandler = function() {
          const formatted = formatPhoneNumber(inputElement.value);
          if (formatted !== inputElement.value) {
            inputElement.value = formatted;
            const ngModelCtrl = angular.element(inputElement).controller('ngModel');
            if (ngModelCtrl) {
              ngModelCtrl.$setViewValue(formatted);
              ngModelCtrl.$render();
            }
          }
        };

        // Remove existing listeners if any
        angular.element(inputElement).off('input', inputHandler);
        angular.element(inputElement).off('blur', blurHandler);

        // Add event listeners
        angular.element(inputElement).on('input', inputHandler);
        angular.element(inputElement).on('blur', blurHandler);

        // Format initial value if present
        if (inputElement.value) {
          const formatted = formatPhoneNumber(inputElement.value);
          if (formatted !== inputElement.value) {
            inputElement.value = formatted;
            const ngModelCtrl = angular.element(inputElement).controller('ngModel');
            if (ngModelCtrl) {
              ngModelCtrl.$setViewValue(formatted);
            }
          }
        }

        // Clear the interval since we found and initialized the input
        if (checkInterval) {
          $timeout.cancel(checkInterval);
          checkInterval = null;
        }
      }

      // Function to find the input element created by x-editable
      function findInputElement() {
        if (inputElement && document.body.contains(inputElement)) {
          return true; // Already initialized and input still exists
        }

        // Try multiple selectors to find the input
        let input = null;

        // Method 1: Look for input in the closest editable form
        const editableForm = element.closest('.editableform');
        if (editableForm.length) {
          input = editableForm.find('input[type="tel"], input[type="text"]')[0];
        }

        // Method 2: Look in the parent's editable container
        if (!input) {
          const editableContainer = element.parent().find('.editable-container, .editable-input');
          if (editableContainer.length) {
            input = editableContainer.find('input[type="tel"], input[type="text"]')[0];
          }
        }

        // Method 3: Look for any input that's a sibling or in nearby DOM
        if (!input) {
          const parent = element.parent();
          input = parent.find('input[type="tel"], input[type="text"].editable-input, input[type="text"].form-control')[0];
        }

        // Method 4: Search in the document for the most recent editable input
        if (!input) {
          const allEditableInputs = angular.element(document).find('.editableform input[type="tel"], .editableform input[type="text"]');
          if (allEditableInputs.length > 0) {
            input = allEditableInputs[allEditableInputs.length - 1];
          }
        }

        if (input && input !== inputElement) {
          inputElement = input;
          initializePhoneFormatting();
          return true;
        }

        return false;
      }

      // Use MutationObserver to watch for when x-editable adds the input to DOM
      let observer = null;
      function startObserving() {
        if (observer || !window.MutationObserver) {
          return;
        }

        observer = new MutationObserver(function(mutations) {
          if (findInputElement()) {
            stopObserving();
          }
        });

        const observeTarget = element.parent().length ? element.parent()[0] : document.body;
        observer.observe(observeTarget, {
          childList: true,
          subtree: true
        });

        if (observeTarget !== document.body) {
          const bodyObserver = new MutationObserver(function(mutations) {
            if (findInputElement()) {
              stopObserving();
              if (bodyObserver) {
                bodyObserver.disconnect();
              }
            }
          });
          bodyObserver.observe(document.body, {
            childList: true,
            subtree: true
          });
          scope.bodyObserver = bodyObserver;
        }
      }

      function stopObserving() {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (scope.bodyObserver) {
          scope.bodyObserver.disconnect();
          scope.bodyObserver = null;
        }
      }

      // Watch for when x-editable shows the input
      element.on('shown', function() {
        stopObserving();
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 0);
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 50);
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 100);
      });

      // Watch for clicks to catch when editing starts
      element.on('click', function() {
        startObserving();
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 50);
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 100);
        $timeout(function() {
          if (findInputElement()) {
            stopObserving();
          }
        }, 150);
      });

      // Periodic check as a fallback
      let checkCount = 0;
      function periodicCheck() {
        if (checkCount++ > 30) {
          if (checkInterval) {
            $timeout.cancel(checkInterval);
            checkInterval = null;
          }
          return;
        }
        if (findInputElement()) {
          if (checkInterval) {
            $timeout.cancel(checkInterval);
            checkInterval = null;
          }
          stopObserving();
        } else {
          checkInterval = $timeout(periodicCheck, 100);
        }
      }

      element.on('click', function() {
        if (!checkInterval) {
          checkCount = 0;
          periodicCheck();
        }
      });

      // Cleanup on destroy
      scope.$on('$destroy', function() {
        if (checkInterval) {
          $timeout.cancel(checkInterval);
        }
        stopObserving();
        if (inputElement) {
          angular.element(inputElement).off('input blur');
        }
        element.off('shown click');
      });
    }
  };
}

export default ['$timeout', editablePhoneDirective];




// WEBPACK FOOTER //
// ./client/common/directives/phone-number/editable-phone.directive.js